import { resolveBackendAssetUrl } from './mediaUrl';

// Image cache: stores normalized path → resolved URL for session stability
const imageCache = new Map<string, string>();

// Session-stable version key (generated once per page load)
// Ensures consistent URLs during a session, avoiding flicker on re-renders
const sessionVersion = Date.now().toString(36);

/**
 * Resolve profile image URL with session-stable caching.
 * - External URLs (http/https) returned as-is
 * - Local images get a session-stable version param for cache busting on deploy/update
 * - Subsequent calls return cached URL to avoid re-renders causing new requests
 * - Call invalidateImageCache() on profile_image_updated event to force refresh
 */
export function resolveProfileImage(raw?: string | null): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;
  
  // External URLs (Google, social auth) pass through unchanged
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  
  // Normalize: single leading slash for server-relative path
  const normalized = '/' + trimmed.replace(/^\/+/, '');
  
  // Return cached URL if available (stable across re-renders)
  if (imageCache.has(normalized)) {
    return imageCache.get(normalized);
  }
  
  // Use session-stable version (not Date.now() per call) for cache key
  // Browser caching + ETag handles revalidation; this just ensures
  // old cached browser entries refresh after deploy/image update
  const resolved = resolveBackendAssetUrl(normalized) || normalized;
  const separator = resolved.includes('?') ? '&' : '?';
  const resolvedUrl = `${resolved}${separator}v=${sessionVersion}`;
  imageCache.set(normalized, resolvedUrl);
  return resolvedUrl;
}

// Clear cached URL for a specific image path (call when profile_image_updated event received)
export function invalidateImageCache(imagePath: string | null | undefined) {
  if (!imagePath) return;
  const trimmed = imagePath.trim();
  if (!trimmed) return;
  
  // Skip external URLs
  if (/^https?:\/\//i.test(trimmed)) return;
  
  const normalized = '/' + trimmed.replace(/^\/+/, '');
  imageCache.delete(normalized);
}
